import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import { getPurchaseInvoices } from '../../purchases/services/purchaseService';

/**
 * Map frontend values to backend expected values
 */
const mapFiltersToBackend = (filters) => {
  // Map document type - only for special cases
  const documentTypeMap = {
    'all': 'All',
    'ticket_de_caisse': 'Receipt',
    'invoice': 'Invoice',
  };

  // Map groupBy - only for special cases
  const groupByMap = {
    'day': 'Daily',
    'week': 'Weekly',
    'month': 'Monthly',
    'quarter': 'Quarterly',
    'year': 'Yearly',
  };

  const backendFilters = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    documentType: documentTypeMap[filters.documentType?.toLowerCase()] || 'All',
    groupBy: groupByMap[filters.groupBy?.toLowerCase()] || 'Daily',
    // ✅ Pass payment method as-is (no hardcoded mapping)
    // If 'all' is selected, send 'All', otherwise send the exact payment method name from API
    paymentMethod: filters.paymentMethod === 'all' ? 'All' : filters.paymentMethod,
  };

  // Add storeId if provided (for CompanyAdmin)
  if (filters.storeId) {
    backendFilters.storeId = filters.storeId;
  }

  return backendFilters;
};

/**
 * Helper to safely convert values to numbers
 */
const safeNumber = (val) => {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
};

const getPurchasePayload = (response) => (
  Array.isArray(response?.payload) ? response.payload : Array.isArray(response) ? response : []
);

const isCashPayment = (paymentMethod) => /cash|espèces|contant/i.test(String(paymentMethod || ''));

const isWithinDateRange = (value, startDate, endDate) => {
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date >= new Date(startDate) && date <= new Date(endDate);
};

export const generateCashFlowReport = async (filters) => {
  const [sales, purchasesResponse] = await Promise.all([
    generateSalesReport({ ...filters, paymentMethod: 'Cash' }),
    getPurchaseInvoices(),
  ]);

  const purchases = getPurchasePayload(purchasesResponse)
    .map((purchase) => ({
      date: purchase.PaidDate || purchase.PaymentDate || purchase.InvoiceDate || purchase.PurchaseDate,
      invoiceNumber: purchase.PurchaseInvoiceNumber || purchase.InvoiceNumber || 'N/A',
      supplierName: purchase.SupplierName || purchase.supplierName || 'Unknown supplier',
      paymentMethod: purchase.PaymentMethod || purchase.PaymentSource || '',
      status: purchase.Status || purchase.status || '',
      amount: safeNumber(purchase.TotalAmount || purchase.totalAmount),
    }))
    .filter((purchase) => (
      /^paid$/i.test(purchase.status)
      && isCashPayment(purchase.paymentMethod)
      && isWithinDateRange(purchase.date, filters.startDate, filters.endDate)
    ));

  const incoming = safeNumber(sales.summary?.totalPaid || sales.summary?.totalSales);
  const outgoing = purchases.reduce((total, purchase) => total + purchase.amount, 0);

  return {
    incoming,
    outgoing,
    netCashFlow: incoming - outgoing,
    purchases,
    sales: sales.details || [],
    metadata: sales.metadata,
  };
};

/**
 * Generate sales report based on filters
 * @param {Object} filters - Report filters (startDate, endDate, documentType, groupBy, paymentMethod)
 * @returns {Promise} - Report data
 */
export const generateSalesReport = async (filters) => {
  try {
    // Map frontend filters to backend expected format
    const backendFilters = mapFiltersToBackend(filters);
    

    const response = await axiosInstance.post('/Reports/sales', backendFilters);


    // Transform backend response to match frontend expectations
    const backendData = response.data.payload;
    
    // Check if we have any data (support both old and new response shapes)
    const hasSalesDetails = Array.isArray(backendData.SalesDetails);
    const hasLegacyDetails = Array.isArray(backendData.TicketDetails) || Array.isArray(backendData.InvoiceDetails);
    if (!hasSalesDetails && !hasLegacyDetails) {

    }
    
    // Transform payment breakdown from array to object - USE BACKEND PERCENTAGE
    const paymentMethods = {};
    backendData.PaymentBreakdown.forEach(payment => {
      paymentMethods[payment.PaymentMethod] = {
        amount: safeNumber(payment.Amount),
        percentage: safeNumber(payment.Percentage), // Use backend percentage directly
        transactionCount: safeNumber(payment.TransactionCount),
      };
    });


    // Transform VAT breakdown from array to object
    const vatBreakdown = {};
    backendData.VATBreakdown.forEach(vat => {
      vatBreakdown[vat.VATRate] = {
        subtotal: safeNumber(vat.SubtotalExclVAT),
        vat: safeNumber(vat.VATAmount),
        total: safeNumber(vat.TotalInclVAT),
      };
    });

    // Build details array — support new unified SalesDetails and legacy TicketDetails/InvoiceDetails
    let details;
    if (hasSalesDetails) {
      details = backendData.SalesDetails.map(item => ({
        date: item.DateTime,
        receiptNumber: item.DocumentNumber,
        documentType: item.DocumentType, // 'Ticket' or 'Invoice'
        customerName: item.CustomerName === 'Walk-in' ? null : item.CustomerName,
        subtotal: safeNumber(item.Subtotal),
        vat: safeNumber(item.VAT),
        total: safeNumber(item.Total),
        paymentMethod: item.PaymentMethod,
        paymentStatus: item.PaymentStatus,
        paidAmount: safeNumber(item.PaidAmount),
        dueAmount: safeNumber(item.DueAmount),
        notes: item.Notes,
        lineItemCount: item.LineItemCount,
        splitPayments: Array.isArray(item.PaymentDetails) ? item.PaymentDetails : [],
      }));
    } else {
      // Legacy format fallback
      details = [
        ...(backendData.TicketDetails || []).map(ticket => ({
          date: ticket.DateTime,
          receiptNumber: ticket.TicketNumber,
          documentType: 'Ticket',
          customerName: ticket.CustomerName === 'Walk-in' ? null : ticket.CustomerName,
          subtotal: safeNumber(ticket.Subtotal),
          vat: safeNumber(ticket.VAT),
          total: safeNumber(ticket.Total),
          paymentMethod: ticket.PaymentMethod,
          paymentStatus: ticket.PaymentStatus,
          paidAmount: safeNumber(ticket.PaidAmount),
          dueAmount: safeNumber(ticket.DueAmount),
          notes: ticket.Notes,
          splitPayments: Array.isArray(ticket.PaymentDetails) ? ticket.PaymentDetails : [],
        })),
        ...(backendData.InvoiceDetails || []).map(invoice => ({
          date: invoice.DateTime,
          receiptNumber: invoice.InvoiceNumber,
          documentType: 'Invoice',
          customerName: invoice.CustomerName,
          subtotal: safeNumber(invoice.Subtotal),
          vat: safeNumber(invoice.VAT),
          total: safeNumber(invoice.Total),
          paymentMethod: invoice.PaymentMethod || 'N/A',
          paymentStatus: invoice.PaymentStatus,
          paidAmount: safeNumber(invoice.PaidAmount),
          dueAmount: safeNumber(invoice.DueAmount),
          notes: invoice.Notes,
          splitPayments: Array.isArray(invoice.PaymentDetails) ? invoice.PaymentDetails : [],
        })),
      ];
    }

    // Sort by date (newest first)
    details.sort((a, b) => new Date(b.date) - new Date(a.date));

    const transformedData = {
      summary: {
        totalSales: safeNumber(backendData.Summary.TotalSales),
        totalVAT: safeNumber(backendData.Summary.TotalVAT),
        totalTransactions: safeNumber(backendData.Summary.Transactions),
        averageSale: safeNumber(backendData.Summary.AvgSale),
        ticketDeCaisseCount: safeNumber(backendData.Summary.TotalTickets),
        invoiceCount: safeNumber(backendData.Summary.TotalInvoices),
        totalSalesExclVAT: safeNumber(backendData.Summary.TotalSalesExclVAT),
        totalPaid: safeNumber(backendData.Summary.TotalPaid),
        totalDue: safeNumber(backendData.Summary.TotalDue),
        paymentBreakdownSummary: backendData.Summary?.PaymentBreakdownSummary || backendData.PaymentBreakdown || [],
      },
      vatBreakdown,
      paymentMethods,
      details,
      metadata: {
        generatedAt: backendData.Metadata.GeneratedAt,
        reportPeriod: backendData.Metadata.ReportPeriod,
      },
    };


    return transformedData;
  } catch (error) {


    throw error;
  }
};

/**
 * Generate daily sale summary report
 * @param {Object} filters - Report filters (startDate, endDate, documentType, paymentMethod)
 * @returns {Promise} - Daily sale summary data
 */
export const generateDailySaleSummaryReport = async (filters) => {
  try {
    // Map document type to backend format
    const documentTypeMap = {
      'all': 'All',
      'ticket_de_caisse': 'Receipt',
      'invoice': 'Invoice',
    };

    const requestPayload = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      documentType: documentTypeMap[filters.documentType?.toLowerCase()] || 'All',
      paymentMethod: filters.paymentMethod === 'all' ? 'All' : filters.paymentMethod,
      storeId: filters.storeId && filters.storeId !== 'all' ? filters.storeId : 0,
    };

    const response = await axiosInstance.post('/Reports/daily-sales-summary', requestPayload);

    const backendData = response.data.payload;

    // Transform response to match frontend expectations
    const transformedData = {
      summary: {
        totalSalesInclVAT: safeNumber(backendData.Totals?.GrandTotalInclVAT),
        totalSalesExclVAT: safeNumber(backendData.Totals?.GrandTotalExclVAT),
        totalVAT: safeNumber(backendData.Totals?.GrandTotalVAT),
        totalItemsSold: safeNumber(backendData.Totals?.TotalItemsSold),
        paymentBreakdownSummary: backendData.Totals?.PaymentBreakdown || [],
      },
      dailySales: (backendData.DailySummary || []).map(day => ({
        Date: day.Date,
        DayOfWeek: day.DayOfWeek,
        FormattedDate: day.FormattedDate,
        TotalDocuments: safeNumber(day.TotalDocuments),
        TicketCount: safeNumber(day.TicketCount),
        InvoiceCount: safeNumber(day.InvoiceCount),
        ItemsSold: safeNumber(day.ItemsSold),
        TotalExclVAT: safeNumber(day.TotalExclVAT),
        TotalVAT: safeNumber(day.TotalVAT),
        TotalInclVAT: safeNumber(day.TotalInclVAT),
        TotalDiscount: safeNumber(day.TotalDiscount),
        TotalPaid: safeNumber(day.TotalPaid),
        TotalDue: safeNumber(day.TotalDue),
        AverageSale: safeNumber(day.AverageSale),
        PaymentBreakdown: day.PaymentBreakdown || [],
      })),
      metadata: {
        generatedAt: backendData.Metadata?.GeneratedAt,
        reportPeriod: backendData.Metadata?.ReportPeriod,
      },
    };

    return transformedData;
  } catch (error) {


    throw error;
  }
};